import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Group, Locale } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Badge, Button, Card, CheckboxField, ConfirmButton, LinkButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { SettingsCards } from '../../components/SettingsCards';
import { useToastOnSuccess } from '../../components/Toast';
import { section, useScopedSettings } from './api';
import { Tabs, useTab } from './Tabs';

function DetailsTab({ group, canEdit, canDelete }: { group: Group; canEdit: boolean; canDelete: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(group.name);
  const [language, setLanguage] = useState<string>(group.defaultLanguage);
  useEffect(() => {
    setName(group.name);
    setLanguage(group.defaultLanguage);
  }, [group]);
  const refresh = async (g: Group) => {
    qc.setQueryData(['groups', 'detail', group.id], g);
    await qc.invalidateQueries({ queryKey: ['groups', 'list'] });
  };
  const save = useMutation({
    mutationFn: () => api.patch(`/groups/${group.id}`, { body: { name, defaultLanguage: language }, schema: Group }),
    onSuccess: refresh,
  });
  const status = useMutation({
    mutationFn: (next: 'active' | 'archived') => api.patch(`/groups/${group.id}`, { body: { status: next }, schema: Group }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/groups/${group.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['groups'] });
      void navigate('/manage/groups');
    },
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  const errs = fieldErrors(save.error);
  const changed = name !== group.name || language !== group.defaultLanguage;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <div className="space-y-6">
      <Card
        footer={
          canEdit ? (
            <Button type="submit" form="group-details" variant="primary" disabled={!changed || save.isPending}>
              {t('common.save')}
            </Button>
          ) : null
        }
      >
        <form id="group-details" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <ErrorLine error={save.error} />
          </div>
          <TextField label={t('groupsUi.field.name')} required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} error={errs['name']} />
          <TextField label={t('groupsUi.field.kind')} value={t(`groupsUi.kind.${group.kind}`)} readOnly disabled />
          <SelectField label={t('groupsUi.field.language')} value={language} onChange={(e) => setLanguage(e.target.value)} disabled={!canEdit}>
            {Locale.options.map((l) => (
              <option key={l} value={l}>
                {t(`sitesUi.language.${l}`)}
              </option>
            ))}
          </SelectField>
        </form>
      </Card>
      <div className="flex flex-wrap gap-2">
        <LinkButton to={`/manage/groups/${group.id}/fields`}>{t('groupsUi.links.fields')}</LinkButton>
        {group.kind === 'repeat' ? <LinkButton to={`/manage/groups/${group.id}/members`}>{t('groupsUi.links.members')}</LinkButton> : null}
      </div>
      {canEdit ? (
        <Card description={t('groupsUi.archive.hint')}>
          <div className="space-y-3">
            <ErrorLine error={status.error} />
            {group.status === 'active' ? (
              <ConfirmButton label={t('groupsUi.archive')} pending={status.isPending} onConfirm={() => status.mutate('archived')} />
            ) : (
              <Button disabled={status.isPending} onClick={() => status.mutate('active')}>
                {t('groupsUi.restore')}
              </Button>
            )}
          </div>
        </Card>
      ) : null}
      {canDelete ? (
        <Card description={t('groupsUi.delete.hint')}>
          <div className="space-y-3">
            <ErrorLine error={remove.error} />
            <ConfirmButton label={t('groupsUi.delete')} confirmLabel={t('groupsUi.delete.confirm')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/** A card with text boxes that save part of the group's `groups` settings. */
function MessageTab({
  groupId,
  name,
  field,
  label,
  hint,
  canEdit,
}: {
  groupId: string;
  name: 'signIn' | 'signOut';
  field: 'welcomeMessage' | 'message';
  label: string;
  hint: string;
  canEdit: boolean;
}) {
  const { query, save, values } = useScopedSettings(`/groups/${groupId}/settings`, 'groups');
  const stored = section<Record<string, string>>(values, name)[field] ?? '';
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => setDraft(null), [values]);
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values) return <Spinner />;
  const text = draft ?? stored;
  return (
    <Card
      footer={
        canEdit ? (
          <Button variant="primary" disabled={draft === null || draft === stored || save.isPending} onClick={() => save.mutate({ [name]: { [field]: text } })}>
            {t('common.save')}
          </Button>
        ) : null
      }
    >
      <div className="space-y-3">
        <ErrorLine error={save.error} />
        <div>
          <label htmlFor={`msg-${name}`} className="mb-1 block text-sm font-medium text-slate-800">
            {label}
          </label>
          <textarea
            id={`msg-${name}`}
            rows={4}
            maxLength={500}
            value={text}
            disabled={!canEdit}
            aria-describedby={`msg-${name}-hint`}
            onChange={(e) => setDraft(e.target.value)}
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100"
          />
          <p id={`msg-${name}-hint`} className="mt-1 text-xs text-slate-600">
            {hint}
          </p>
        </div>
      </div>
    </Card>
  );
}

function KioskPin({ group, canEdit }: { group: Group; canEdit: boolean }) {
  const qc = useQueryClient();
  const [pin, setPin] = useState('');
  const setter = useMutation({
    mutationFn: (value: string | null) => api.put(`/groups/${group.id}/kiosk-pin`, { body: { pin: value }, schema: Group }),
    onSuccess: async (g) => {
      setPin('');
      qc.setQueryData(['groups', 'detail', group.id], g);
      await qc.invalidateQueries({ queryKey: ['groups', 'list'] });
    },
  });
  useToastOnSuccess(setter.isSuccess, t('common.saved'));
  return (
    <Card title={t('groupsUi.kiosk.pin')} description={t('groupsUi.kiosk.pinHint')}>
      <div className="space-y-3">
        <ErrorLine error={setter.error} />
        <p className="text-sm">{group.hasKioskPin ? t('groupsUi.kiosk.pinSet') : t('groupsUi.kiosk.pinNone')}</p>
        {canEdit ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setter.mutate(pin);
            }}
          >
            <TextField
              label={t('groupsUi.kiosk.pinNew')}
              inputMode="numeric"
              autoComplete="off"
              pattern="\d{4}"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            />
            <Button type="submit" variant="primary" disabled={!/^\d{4}$/.test(pin) || setter.isPending}>
              {t('groupsUi.kiosk.pinSave')}
            </Button>
            {group.hasKioskPin ? (
              <Button disabled={setter.isPending} onClick={() => setter.mutate(null)}>
                {t('groupsUi.kiosk.pinRemove')}
              </Button>
            ) : null}
          </form>
        ) : null}
      </div>
    </Card>
  );
}

const KIOSK_FLAGS = [
  ['showOnKiosk', 'groupsUi.kiosk.show', false],
  ['allowNewMembersFromKiosk', 'groupsUi.kiosk.newMembers', true],
  ['allowMembersUpdatePhoto', 'groupsUi.kiosk.updatePhoto', true],
] as const;

function KioskTab({ group, canEdit }: { group: Group; canEdit: boolean }) {
  const { query, save, values } = useScopedSettings(`/groups/${group.id}/settings`, 'groups');
  const stored = section<Record<string, unknown>>(values, 'kiosk');
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  useEffect(() => setDraft({}), [values]);
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values) return <Spinner />;
  const merged = { ...stored, ...draft };
  const changed = Object.keys(draft).some((k) => draft[k] !== stored[k]);
  return (
    <div className="space-y-6">
      <Card
        footer={
          canEdit ? (
            <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate({ kiosk: merged })}>
              {t('common.save')}
            </Button>
          ) : null
        }
      >
        <div className="space-y-4">
          <ErrorLine error={save.error} />
          {KIOSK_FLAGS.filter(([, , repeatOnly]) => !repeatOnly || group.kind === 'repeat').map(([key, label]) => (
            <CheckboxField
              key={key}
              label={t(label)}
              checked={Boolean(merged[key])}
              disabled={!canEdit}
              onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.checked }))}
            />
          ))}
          <SelectField
            label={t('groupsUi.kiosk.nameDisplay')}
            value={String(merged['nameDisplay'] ?? 'full')}
            disabled={!canEdit}
            onChange={(e) => setDraft((d) => ({ ...d, nameDisplay: e.target.value }))}
          >
            <option value="full">{t('groupsUi.kiosk.nameFull')}</option>
            <option value="first_name_initial">{t('groupsUi.kiosk.nameInitial')}</option>
          </SelectField>
        </div>
      </Card>
      <KioskPin group={group} canEdit={canEdit} />
    </div>
  );
}

export function GroupDetailPage() {
  const { id = '' } = useParams();
  const canManage = useCan('groups.manage');
  const canDelete = useCan('groups.delete');
  const q = useQuery({ queryKey: ['groups', 'detail', id], queryFn: () => api.get(`/groups/${id}`, { schema: Group }) });
  const tabs = [
    { key: 'details', label: t('groupsUi.tab.details') },
    { key: 'signin', label: t('groupsUi.tab.signIn') },
    { key: 'signout', label: t('groupsUi.tab.signOut') },
    { key: 'kiosk', label: t('groupsUi.tab.kiosk') },
    { key: 'data', label: t('groupsUi.tab.data') },
  ];
  const [tab, setTab] = useTab(tabs);
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const group = q.data;
  const canEdit = canManage && group.status === 'active';
  return (
    <>
      <p className="mb-2 text-sm">
        <Link className="text-brand hover:underline" to="/manage/groups">
          {t('groupsUi.back')}
        </Link>
      </p>
      <PageHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {group.name}
            <Badge tone={group.kind === 'repeat' ? 'good' : 'neutral'}>{t(`groupsUi.kind.${group.kind}`)}</Badge>
            {group.status === 'archived' ? <Badge>{t('groupsUi.archivedBadge')}</Badge> : null}
          </span>
        }
      />
      <Tabs tabs={tabs} current={tab} onChange={setTab} label={t('groupsUi.tabs')}>
        {tab === 'details' ? <DetailsTab group={group} canEdit={canManage} canDelete={canDelete} /> : null}
        {tab === 'signin' ? (
          <MessageTab groupId={id} name="signIn" field="welcomeMessage" label={t('groupsUi.signIn.welcome')} hint={t('groupsUi.signIn.welcomeHint')} canEdit={canEdit} />
        ) : null}
        {tab === 'signout' ? (
          <MessageTab groupId={id} name="signOut" field="message" label={t('groupsUi.signOut.message')} hint={t('groupsUi.signOut.messageHint')} canEdit={canEdit} />
        ) : null}
        {tab === 'kiosk' ? <KioskTab group={group} canEdit={canEdit} /> : null}
        {tab === 'data' ? (
          <div className="space-y-4">
            <h2 className="sr-only">{t('groupsUi.data.title')}</h2>
            <SettingsCards scope="groups" scopeId={id} canEdit={canEdit} exclude={['groups']} />
          </div>
        ) : null}
      </Tabs>
    </>
  );
}
